import { computePosition, autoUpdate, shift, flip, offset } from "@floating-ui/dom";
import * as Component from "../component";
import * as Keyboard from "../keyboard";
import * as UI from "../ui";

UI.onDataActionClick("dropdown:trigger-click", ({ el, event }) => {
  const dropdownEl = getDropdownAncestor(el);
  const isOpen = toggle(dropdownEl);

  // A keyboard-activated click carries no pointer position; focus should then move into the menu.
  const openedByKeyboard = event.detail === 0;
  if (isOpen && openedByKeyboard) {
    Keyboard.focusFirst(getDropdownBody(dropdownEl));
  }
});

document.addEventListener("dropdown:click-away", (event) => {
  const dropdownEl = getDropdownAncestor(event.target);
  if (!isDropdownOpen(dropdownEl)) return;
  toggle(dropdownEl, false);
});

UI.onDataActionClick("dropdown:close", ({ el, target }) => {
  const dropdownEl = target ?? el.closest(".dropdown");
  if (!isDropdownOpen(dropdownEl)) return;
  toggle(dropdownEl, false);
});

/**
 * Check if a dropdown is open.
 *
 * @param {HTMLElement} dropdownEl - The dropdown element
 * @returns {Boolean} - The current state of the dropdown
 */
function isDropdownOpen(dropdownEl) {
  return dropdownEl.classList.contains("dropdown--open");
}

/**
 * Toggles the dropdown open or closed.
 *
 * @param {HTMLElement} dropdownEl - The dropdown element
 * @param {Boolean} [open] - The state to set the dropdown to
 * @returns {Boolean} - Whether the dropdown is now open
 */
export function toggle(dropdownEl, open) {
  const popoverEl = getDropdownBody(dropdownEl);
  const wasOpen = isDropdownOpen(dropdownEl);

  let isOpen = togglePopover(popoverEl, dropdownEl, open);

  // Idempotent: re-toggling to the current state (e.g. ArrowRight onto an already-open submenu) must not
  // re-attach the listeners, which would double every keystroke.
  if (isOpen === wasOpen) return isOpen;

  const triggerEl = getDropdownTrigger(dropdownEl);
  triggerEl.ariaExpanded = isOpen.toString();

  // Reflect the state before wiring the open listeners: they read the body's visibility (e.g. taking the items
  // out of the tab order only finds them once `dropdown--open` has made the body displayed).
  dropdownEl.classList.toggle("dropdown--open", isOpen);

  if (isOpen) {
    const placement = dropdownPlacement(popoverEl);
    const middleware = opensToSide(dropdownEl) ? sideMiddleware() : [shift(), flip()];

    const cleanup = autoUpdate(dropdownEl, popoverEl, async () => {
      const { x, y } = await computePosition(dropdownEl, popoverEl, {
        placement,
        middleware,
      });

      popoverEl.style.left = `${x}px`;
      popoverEl.style.top = `${y}px`;
    });

    Component.setExtra(popoverEl, "cleanup", cleanup);

    attachOpenEventListeners(dropdownEl);
  } else {
    const cleanup = Component.popExtra(popoverEl, "cleanup");
    if (cleanup) {
      cleanup();
    }
    detachOpenEventListeners(dropdownEl);
    closeOpenSubmenus(popoverEl);
  }

  dropdownEl.dispatchEvent(createToggleEvent(isOpen));

  return isOpen;
}

function attachOpenEventListeners(dropdownEl) {
  const keyboardNav = Keyboard.initListNavigation({
    // A submenu's trigger is an item of the parent menu, so arrow keys on it must drive the parent (it is entered
    // with ArrowRight/Enter). Only a top-level trigger enters its own menu with the arrows.
    triggerEl: isNestedDropdown(dropdownEl) ? null : getDropdownTrigger(dropdownEl),
    containerEl: getDropdownBody(dropdownEl),
    handle: (event) => handleMenuKey(event, dropdownEl),
    // A menu cycles through its items; leaving happens via Escape/ArrowLeft, not by arrowing onto the trigger.
    wrap: true,
  });

  Component.setExtra(dropdownEl, "keyboardNav", keyboardNav);

  // Tabbing (or clicking) focus out of the dropdown closes it, so an open menu always holds the focus.
  const closeOnFocusLeave = (event) => {
    const movingTo = event.relatedTarget;
    if (movingTo && !dropdownEl.contains(movingTo)) {
      toggle(dropdownEl, false);
    }
  };
  dropdownEl.addEventListener("focusout", closeOnFocusLeave);
  Component.setExtra(dropdownEl, "closeOnFocusLeave", closeOnFocusLeave);
}

// stopPropagation keeps a key from also acting on the ancestor menus it bubbles through.
function handleMenuKey(event, dropdownEl) {
  if (event.key === "Escape") {
    closeAndRefocusTrigger(dropdownEl);
    event.stopPropagation();
    return true;
  }

  // Plain arrows only, same as the list navigation.
  if (event.altKey || event.ctrlKey || event.metaKey) return;

  // Left/Right drive side-opening submenus only; a menu that drops below is entered with Enter and left with Escape.
  if (event.key === "ArrowLeft" && opensToSide(dropdownEl)) {
    closeAndRefocusTrigger(dropdownEl);
    event.stopPropagation();
    return true;
  }

  if (event.key === "ArrowRight") {
    const submenu = focusedSubmenu(dropdownEl);
    if (submenu && opensToSide(submenu)) {
      toggle(submenu, true);
      Keyboard.focusFirst(getDropdownBody(submenu));
      event.stopPropagation();
      return true;
    }
  }
}

function closeAndRefocusTrigger(dropdownEl) {
  const triggerEl = getDropdownTrigger(dropdownEl);
  toggle(dropdownEl, false);
  triggerEl?.focus();
}

function focusedSubmenu(dropdownEl) {
  const focusedEl = document.activeElement;
  if (!focusedEl?.matches("[data-dropdown-trigger]")) return null;

  const submenu = focusedEl.closest(".dropdown");
  const isOwnSubmenu = submenu !== dropdownEl && getDropdownBody(dropdownEl).contains(submenu);
  return isOwnSubmenu ? submenu : null;
}

function detachOpenEventListeners(dropdownEl) {
  const keyboardNav = Component.popExtra(dropdownEl, "keyboardNav");
  if (keyboardNav !== undefined) {
    keyboardNav.teardown();
  }

  const closeOnFocusLeave = Component.popExtra(dropdownEl, "closeOnFocusLeave");
  if (closeOnFocusLeave !== undefined) {
    dropdownEl.removeEventListener("focusout", closeOnFocusLeave);
  }
}

function getDropdownAncestor(element) {
  return element.closest(".dropdown");
}

function isNestedDropdown(dropdownEl) {
  return dropdownEl.parentElement?.closest(".dropdown") != null;
}

function opensToSide(dropdownEl) {
  return getDropdownBody(dropdownEl).dataset.placement !== "bottom";
}

function closeOpenSubmenus(popoverEl) {
  popoverEl.querySelectorAll(".dropdown--open").forEach((submenu) => toggle(submenu, false));
}

function dropdownPlacement(popoverEl) {
  const side = popoverEl.dataset.placement;
  const align = popoverEl.dataset.align;
  return align == "center" ? side : `${side}-${align}`;
}

// A side-opening dropdown: the offset supplies the side gap (mainAxis) and lifts the body by its own top
// padding (crossAxis) so the first item lines up with the trigger row instead of sitting a padding below it.
function sideMiddleware() {
  const gap = nestedGap();
  return [offset({ mainAxis: gap, crossAxis: -gap }), flip(), shift({ padding: gap })];
}

let cachedNestedGap;

// Read lazily: in dev the CSS is injected after this module loads, so --space-02 isn't resolvable at import time.
function nestedGap() {
  if (cachedNestedGap === undefined) {
    const rootStyle = getComputedStyle(document.documentElement);
    cachedNestedGap = parseFloat(rootStyle.getPropertyValue("--space-02")) * parseFloat(rootStyle.fontSize);
  }
  return cachedNestedGap;
}

function getDropdownTrigger(dropdownEl) {
  return dropdownEl.querySelector("[data-dropdown-trigger]");
}

function getDropdownBody(dropdownEl) {
  return dropdownEl.querySelector(".dropdown__body");
}

function createToggleEvent(open) {
  return new CustomEvent("dropdown:toggle", {
    bubbles: true,
    detail: { open },
  });
}

// If available Popover API will promote the dropdown body to the front of the UI,
// if not, falls back to applying a the dropdown fallback class.
const togglePopover =
  "popover" in HTMLElement.prototype
    ? (popoverEl, _dropdownEl, open) => {
        let isOpen = popoverEl.togglePopover(open);
        // On older browser, `togglePopover` returns `undefined`,
        // manually determine the new state
        if (typeof isOpen === "undefined") {
          isOpen = popoverEl.matches(":popover-open");
        }
        return isOpen;
      }
    : (_popoverEl, dropdownEl, open) => dropdownEl.classList.toggle("dropdown--open--fallback", open);

export function initForAlpine(Alpine) {
  Alpine.magic("dropdown", () => ({
    toggle,
  }));
}
